import api from "@/api";
import i18n from "@/i18n";

const { t } = i18n.global;
export default {
    state: {
        mandates: [],
        mandatesLoading: false,
        mandateEmailModal: {
            display: false,
            mandateId: null,
        },
    },
    getters: {
        mandates: (state) => state.mandates,
        mandatesLoading: (state) => state.mandatesLoading,
        mandateEmailModal: (state) => state.mandateEmailModal,
    },
    mutations: {
        setMandates(state, mandates) {
            state.mandates = mandates;
        },
        setMandatesLoading(state, status) {
            state.mandatesLoading = status;
        },
        setMandateEmailModal(state, payload) {
            state.mandateEmailModal = payload;
        }
    },
    actions: {
        async fetchMandates({ commit }, customerId) {
            if (!customerId) {
                commit('setMandates', []);
                return;
            }

            commit('setMandatesLoading', true);
            try {
                const response = await api({ requiresAuth: true })
                    .get(`/v1/customers/${customerId}/mandates`);

                const data = response.data.data || response.data || [];
                commit('setMandates', data);
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToFetchMandates")
                }, { root: true });
                commit('setMandates', []);
                throw error;
            } finally {
                commit('setMandatesLoading', false);
            }
        },

        async createMandate({ dispatch, commit }, { customer_id, mandate_type_id, customer_mandate_mode_id, agent_id, notice_agent_id, deliveries, files }) {
            try {
                const payload = {
                    customer_id,
                    mandate_type_id,
                    customer_mandate_mode_id,
                    agent_id,
                    notice_agent_id,
                    deliveries,
                };

                if (files && files.length > 0) {
                    payload.files = [];
                    for (const file of files) {
                        const base64 = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result.split(',')[1]);
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                        });
                        payload.files.push({
                            name: file.name,
                            type: file.type,
                            data: base64
                        });
                    }
                }

                await api({ requiresAuth: true }).post(`v1/customers/${customer_id}/mandates`, payload);

                commit("setToast", {
                    display: true,
                    type: "success",
                    message: t("messages.mandateCreatedSuccessfully"),
                }, { root: true });

                await dispatch("fetchMandates", customer_id);

            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToCreateMandate")
                }, { root: true });
                throw error;
            }
        },

        async downloadMandatePDF({ commit }, { customerId, mandateId, name }) {
            try {
                const response = await api({ requiresAuth: true }).get(
                    `/v1/customers/${customerId}/mandates/${mandateId}/generate-pdf`,
                    { responseType: 'blob' }
                );

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Mandat ${name}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToDownloadMandate")
                }, { root: true });
                throw error;
            }
        },

        async getMandateCompanyDeliveryDrafts({ commit }, { customer_id, mandate_id }) {
            try {
                const response = await api({ requiresAuth: true }).get(
                    `/v1/customers/${customer_id}/mandates/${mandate_id}/company-deliveries-drafts`
                );

                return response.data;
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToFetchDeliveryDrafts")
                }, { root: true });
                throw error;
            }
        },

        async sendMandateEmail({ commit }, { customer_id, mandate_id, deliveries }) {
            try {
                const response = await api({ requiresAuth: true }).post(
                    `/v1/customers/${customer_id}/mandates/${mandate_id}/company-deliveries`,
                    {
                        deliveries,
                    }
                );

                return response.data;
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToSendMandateEmail")
                }, { root: true });
                throw error;
            }
        },

        async updateDeliveryStatus({ commit }, { deliveryId, status }) {
            try {
                const statusId = status.id || status;
                await api({ requiresAuth: true }).patch(
                    `/v1/customer-mandate-company-deliveries/${deliveryId}/status`,
                    {
                        status_id: statusId,
                    }
                );
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToUpdateDeliveryStatus")
                }, { root: true });
                throw error;
            }
        },

        addMandateFiles: async ({ commit }, { mandateId, files }) => {
            try {
                const formData = new FormData();

                files.forEach((file) => {
                    formData.append("files[]", file);
                });
                const response = await api({ requiresAuth: true }).post(
                    `/v1/customer-mandates/${mandateId}/files`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        }
                    }
                );

                const mandateData = response.data.data || response.data;
                if (mandateData && mandateData.files) {
                    commit("setFilesToEdit", mandateData.files, { root: true });
                }
                return mandateData;
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToUploadMandateFiles")
                }, { root: true });
                throw error;
            }
        },

        fetchMandateFiles: async ({ commit }, { customerId, mandateId }) => {
            try {
                const response = await api({ requiresAuth: true })
                    .get(`/v1/customers/${customerId}/mandates`);

                const mandates = response.data.data || response.data || [];

                commit('setMandates', mandates);

                const mandate = mandates.find(m => m.id === mandateId);

                if (mandate && mandate.files) {
                    return { files: mandate.files };
                }

                return { files: [] };
            } catch (error) {
                commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || t("messages.failedToFetchMandateFiles")
                }, { root: true });
                throw error;
            }
        },
    }
}
